/**
 * DEMANDES PÉRIODIQUES (US-028, US-029) — la règle et son échéance, écrites
 * une fois : l'écran les affiche, l'ordonnanceur de l'API les applique.
 *
 * Les dates sont des jours calendaires « AAAA-MM-JJ », sans heure : une
 * demande périodique naît le jour dit, à la première passe de l'ordonnanceur.
 */
export const FREQUENCES = ['quotidienne', 'hebdomadaire', 'mensuelle', 'annuelle'];
export const FREQUENCE_LABELS = {
    quotidienne: 'Chaque jour ouvré',
    hebdomadaire: 'Chaque semaine',
    mensuelle: 'Chaque mois',
    annuelle: 'Chaque année',
};
/** 1 = lundi … 5 = vendredi (on ne crée pas de demande le week-end). */
export const JOURS_SEMAINE = [1, 2, 3, 4, 5];
export const JOUR_LABELS = { 1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi', 5: 'Vendredi' };
/** Le refus d'une règle mal formée, pour celui qui la saisit ; null si elle tient. */
export function refusRegle(r) {
    if (!FREQUENCES.includes(r.frequence))
        return 'Choisissez une fréquence.';
    if (r.frequence === 'hebdomadaire') {
        if (!r.joursSemaine?.length || r.joursSemaine.some((j) => !JOURS_SEMAINE.includes(j))) {
            return 'Cochez au moins un jour, du lundi au vendredi.';
        }
    }
    if (r.frequence === 'mensuelle' || r.frequence === 'annuelle') {
        if (!r.jourMois || r.jourMois < 1 || r.jourMois > 31)
            return 'Le jour du mois va de 1 à 31.';
    }
    if (r.frequence === 'annuelle' && (!r.mois || r.mois < 1 || r.mois > 12))
        return 'Choisissez le mois.';
    return null;
}
const iso = (d) => d.toISOString().slice(0, 10);
const jourUTC = (s) => new Date(`${s}T00:00:00Z`);
const dernierJourDuMois = (annee, mois0) => new Date(Date.UTC(annee, mois0 + 1, 0)).getUTCDate();
/**
 * La PREMIÈRE échéance STRICTEMENT après `apres` (AAAA-MM-JJ). Strictement :
 * une échéance qui vient d'être générée ne se regénère pas le même jour.
 */
export function prochaineEcheance(r, apres) {
    const d = jourUTC(apres);
    for (let i = 0; i < 800; i++) {
        d.setUTCDate(d.getUTCDate() + 1);
        const js = d.getUTCDay(); // 0 dimanche … 6 samedi
        const ouvre = js >= 1 && js <= 5;
        const jour = d.getUTCDate();
        const dernier = dernierJourDuMois(d.getUTCFullYear(), d.getUTCMonth());
        const jourVise = Math.min(r.jourMois ?? 1, dernier);
        if (r.frequence === 'quotidienne' && ouvre)
            return iso(d);
        if (r.frequence === 'hebdomadaire' && r.joursSemaine?.includes(js))
            return iso(d);
        if (r.frequence === 'mensuelle' && jour === jourVise)
            return iso(d);
        if (r.frequence === 'annuelle' && d.getUTCMonth() + 1 === r.mois && jour === jourVise)
            return iso(d);
    }
    throw new Error('Règle de périodicité sans échéance.');
}
/** « Chaque semaine : lundi, jeudi » — la règle lue par un humain. */
export function decrireRegle(r) {
    switch (r.frequence) {
        case 'quotidienne':
            return 'Chaque jour ouvré';
        case 'hebdomadaire':
            return `Chaque semaine : ${(r.joursSemaine ?? []).map((j) => JOUR_LABELS[j]?.toLowerCase()).join(', ')}`;
        case 'mensuelle':
            return `Chaque mois, le ${r.jourMois}`;
        case 'annuelle':
            return `Chaque année, le ${r.jourMois}/${String(r.mois).padStart(2, '0')}`;
    }
}
/* ------------------------------------------------------------------ */
/* Pièces jointes (US-012, US-013, ENF-2A)                             */
/* ------------------------------------------------------------------ */
export const PJ_TAILLE_MAX_OCTETS = 20 * 1024 * 1024;
export const PJ_NOMBRE_MAX_PAR_ENVOI = 10;
/**
 * Types acceptés : images, PDF, bureautique, texte, archives, courriels.
 * Les exécutables et scripts sont refusés — le fichier est servi à d'autres
 * personnes que celle qui l'a déposé.
 */
export const PJ_EXTENSIONS = [
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic',
    'pdf', 'txt', 'csv', 'log',
    'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf',
    'zip', '7z', 'eml', 'msg',
];
export function refusPieceJointe(nom, tailleOctets) {
    const ext = nom.toLowerCase().split('.').pop() ?? '';
    if (!nom.includes('.') || !PJ_EXTENSIONS.includes(ext)) {
        return `Ce type de fichier n'est pas accepté (${ext || 'sans extension'}).`;
    }
    if (tailleOctets > PJ_TAILLE_MAX_OCTETS)
        return `${nom} dépasse 20 Mo.`;
    if (tailleOctets === 0)
        return `${nom} est vide.`;
    return null;
}
//# sourceMappingURL=periodicite.js.map